'use client';

import { RadialBar, RadialBarChart, type TooltipProps } from 'recharts';
import { CardContent } from '@ui/card.tsx';
import { type ChartConfig, ChartContainer, ChartTooltip } from '@r/components/ui/chart.tsx';
import { FormattedNumber } from '@r/components/formatted-number.tsx';
import { useColorGradient } from '@client/hooks/use-color-gradient.tsx';
import type { FC } from 'react';
import type { InsightUserInsightUserMeReadModel } from '@r/api/generated/client.schemas.ts';

type UserInsights = InsightUserInsightUserMeReadModel;

interface RadialDataPoint {
  name: string;
  value: number;
  fill: string;
  originalKey: string;
}

const RadialChart = ({
  userInsights,
  isTargetPointLogic = false
}: {
  userInsights: UserInsights;
  isTargetPointLogic?: boolean;
}) => {
  const rawDataPerMonth = userInsights?.insightPerformance?.targetsAmountPerMonth || [];

  const allTargetReferences = new Set<string>();
  for (const monthData of rawDataPerMonth) {
    for (const target of monthData.targets) {
      allTargetReferences.add(target.targetReference);
    }
  }
  const objectiveKeys: string[] = Array.from(allTargetReferences);

  const cumulativeTotals: { [key: string]: number } = {};
  for (const key of objectiveKeys) {
    cumulativeTotals[key] = 0;
  }

  for (const monthData of rawDataPerMonth) {
    for (const target of monthData.targets) {
      if (target.targetReference in cumulativeTotals) {
        cumulativeTotals[target.targetReference] += target.totalAmount || 0;
      }
    }
  }

  const radialData: { name: string; value: number; fill: string; originalKey: string }[] = [];
  const dynamicColors = useColorGradient(objectiveKeys.length);
  const colorMap: { [key: string]: string } = {};
  objectiveKeys.forEach((key, index) => {
    colorMap[key] = dynamicColors[index];
  });

  for (const key of objectiveKeys) {
    const totalCumulativeValue = cumulativeTotals[key] || 0;
    const color = colorMap[key] || 'hsl(var(--muted))';

    radialData.push({
      name: key,
      value: totalCumulativeValue,
      fill: color,
      originalKey: key
    });
  }

  const chartConfig: ChartConfig = {};
  for (const item of radialData) {
    chartConfig[item.name] = {
      label: item.name,
      color: item.fill
    };
  }

  type CustomTooltipProps = TooltipProps<number, string> & {
    active?: boolean;
    payload?: {
      payload: RadialDataPoint;
    }[];
  };

  const CustomRadialTooltip: FC<CustomTooltipProps> = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0].payload;

      if (dataPoint && dataPoint.name !== undefined && dataPoint.value !== undefined) {
        const objectiveName = dataPoint.name;
        const cumulativeAmount = dataPoint.value;
        const color = dataPoint.fill || 'hsl(var(--primary))';
        const formattedValue: number = cumulativeAmount;

        return (
          <div className="p-2 text-sm rounded-md border shadow-md bg-light">
            <div className="flex items-center">
              <div className="mr-2 w-3 h-3 rounded-sm" style={{ backgroundColor: color }} />
              <div className="font-medium text-foreground">{objectiveName} :</div>
              <div className="ml-1 font-bold text-foreground">
                <FormattedNumber
                  value={formattedValue}
                  style={isTargetPointLogic ? 'decimal' : undefined}
                />
              </div>
            </div>
          </div>
        );
      }
    }
    return null;
  };

  return (
    <CardContent>
      <ChartContainer config={chartConfig} className="mx-auto aspect-square max-h-[220px]">
        <RadialBarChart
          data={radialData}
          innerRadius={50}
          outerRadius={80}
          startAngle={120}
          endAngle={-190}
        >
          <ChartTooltip cursor={false} content={<CustomRadialTooltip />} />
          <RadialBar dataKey="value" cornerRadius={100} className="cursor-pointer" />
        </RadialBarChart>
      </ChartContainer>
    </CardContent>
  );
};

export { RadialChart };
